import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Globe, DollarSign, Package, ShoppingCart, Link2, Lock,
  Plus, Loader2, ExternalLink, CheckCircle2, TrendingUp, Construction
} from "lucide-react";
import authService from "../../services/authService";
import AvisoAutomacao24h from "../AvisoAutomacao24h";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/usa`;

/**
 * Painel do afiliado que vende internacionalmente (fora do Brasil).
 *
 * Regra de negócio: é um plano pago separado do plano Brasil - se o
 * usuário não tiver o plano "internacional" ativo, o backend recusa
 * (403) e esta tela mostra a chamada para upgrade em vez de dados vazios
 * fingindo que funciona.
 */
const AfiliadoUSA = () => {
  const usuarioId = authService.getUser()?.id || "";

  const [carregando, setCarregando] = useState(true);
  const [semAcesso, setSemAcesso] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [marketplaces, setMarketplaces] = useState([]);
  const [integracoes, setIntegracoes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [abaAtiva, setAbaAtiva] = useState("dashboard");
  const [conectando, setConectando] = useState(null);

  const carregarTudo = async () => {
    setCarregando(true);
    setSemAcesso(false);
    try {
      const [respDash, respMkt, respInt, respProd, respPed] = await Promise.all([
        authService.fetchAuth(`${API}/dashboard/${usuarioId}`),
        authService.fetchAuth(`${API}/marketplaces`),
        authService.fetchAuth(`${API}/integracoes/${usuarioId}`),
        authService.fetchAuth(`${API}/produtos/${usuarioId}`),
        authService.fetchAuth(`${API}/pedidos/${usuarioId}`),
      ]);

      if (respDash.status === 403) {
        setSemAcesso(true);
        return;
      }

      if (respDash.ok) setDashboard(await respDash.json());
      if (respMkt.ok) setMarketplaces((await respMkt.json()).marketplaces || []);
      if (respInt.ok) setIntegracoes(await respInt.json());
      if (respProd.ok) setProdutos(await respProd.json());
      if (respPed.ok) setPedidos(await respPed.json());
    } catch (error) {
      // CORRIGIDO (achado real): qualquer erro que não fosse 403 (rede,
      // 500, timeout) caía aqui sem marcar semAcesso - a tela tentava
      // desenhar o painel completo com dados vazios, resultando numa
      // tela preta/quebrada em vez da tela segura de "Em construção".
      console.error("Erro ao carregar painel USA:", error);
      setSemAcesso(true);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarTudo();
  }, []);

  const conectarMarketplace = async (marketplaceId) => {
    setConectando(marketplaceId);
    try {
      const resp = await authService.fetchAuth(`${API}/integracoes/conectar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ afiliado_id: usuarioId, marketplace_id: marketplaceId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao conectar marketplace");
      toast.success("Marketplace conectado com sucesso!");
      await carregarTudo();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setConectando(null);
    }
  };

  const marketplacesConectadosIds = integracoes.map((i) => i.marketplace_id);

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando painel internacional...
      </div>
    );
  }

  if (semAcesso) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="p-8 text-center bg-gray-800/50 border-gray-700">
          <Construction className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Painel Internacional — Em construção</h2>
          <p className="text-gray-400 mb-6">
            Vender fora do Brasil (Amazon US, Walmart, TikTok Shop e mais) ainda está em construção,
            com regras próprias sendo preparadas. Se você tem interesse, fale com o administrador.
          </p>
          <a
            href={`https://wa.me/5521964894355?text=${encodeURIComponent("Olá! Sou afiliado ClickHub e tenho interesse no Painel Internacional.")}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2.5 rounded-lg"
          >
            <Globe className="w-4 h-4 mr-2" />
            Falar com Suporte
          </a>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Globe className="w-7 h-7 text-blue-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Painel Internacional</h1>
          <p className="text-sm text-gray-400">Vendas fora do Brasil, em dólar (USD)</p>
        </div>
      </div>

      <AvisoAutomacao24h usuarioId={usuarioId} />

      {/* Abas */}
      <div className="flex gap-2 mb-6 border-b border-gray-700">
        {[
          { id: "dashboard", label: "Dashboard", icon: TrendingUp },
          { id: "marketplaces", label: "Marketplaces", icon: Link2 },
          { id: "produtos", label: "Produtos", icon: Package },
          { id: "pedidos", label: "Pedidos", icon: ShoppingCart },
        ].map((aba) => {
          const Icone = aba.icon;
          return (
            <button
              key={aba.id}
              onClick={() => setAbaAtiva(aba.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                abaAtiva === aba.id ? "border-blue-500 text-blue-400" : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              <Icone className="w-4 h-4" />
              {aba.label}
            </button>
          );
        })}
      </div>

      {/* Dashboard */}
      {abaAtiva === "dashboard" && dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gray-800/50 border-gray-700">
            <p className="text-xs text-gray-500">Receita (USD)</p>
            <p className="text-2xl font-bold text-green-400">${(dashboard.receita_usd || 0).toFixed(2)}</p>
          </Card>
          <Card className="p-4 bg-gray-800/50 border-gray-700">
            <p className="text-xs text-gray-500">Comissão (USD)</p>
            <p className="text-2xl font-bold text-blue-400">${(dashboard.comissao_usd || 0).toFixed(2)}</p>
          </Card>
          <Card className="p-4 bg-gray-800/50 border-gray-700">
            <p className="text-xs text-gray-500">Produtos</p>
            <p className="text-2xl font-bold text-white">{dashboard.total_produtos || 0}</p>
          </Card>
          <Card className="p-4 bg-gray-800/50 border-gray-700">
            <p className="text-xs text-gray-500">Marketplaces conectados</p>
            <p className="text-2xl font-bold text-white">{dashboard.integracoes_ativas || 0}</p>
          </Card>
        </div>
      )}

      {/* Marketplaces */}
      {abaAtiva === "marketplaces" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {marketplaces.map((m) => {
            const conectado = marketplacesConectadosIds.includes(m.id);
            return (
              <Card key={m.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{m.name}</p>
                  <p className="text-xs text-gray-500">{m.category === "social" ? "Rede social com loja" : "Marketplace"}</p>
                </div>
                {conectado ? (
                  <span className="flex items-center gap-1 text-green-400 text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Conectado
                  </span>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => conectarMarketplace(m.id)}
                    disabled={conectando === m.id}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {conectando === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                    Conectar
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Produtos */}
      {abaAtiva === "produtos" && (
        <div className="space-y-2">
          {produtos.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhum produto internacional vinculado ainda.
            </Card>
          )}
          {produtos.map((p) => (
            <Card key={p.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
              <p className="text-white">{p.nome}</p>
              <p className="text-green-400 font-bold">${(p.preco_usd || 0).toFixed(2)}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Pedidos */}
      {abaAtiva === "pedidos" && (
        <div className="space-y-2">
          {pedidos.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhum pedido internacional ainda.
            </Card>
          )}
          {pedidos.map((p) => (
            <Card key={p.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
              <div>
                <p className="text-white">{p.produto_nome || p.id}</p>
                <p className="text-xs text-gray-500">{p.status}</p>
              </div>
              <p className="text-green-400 font-bold">${(p.valor_usd || 0).toFixed(2)}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AfiliadoUSA;
